// src/components/property/PropertySceneList.tsx
/**
 * PropertySceneList Component
 * Drag-and-drop sortable list of property scenes
 * Uses @dnd-kit for accessible reordering
 */

import React, { memo, useCallback } from 'react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDndSensors } from '@/hooks/useDndSensors';
import { PropertySceneCardMemo } from './PropertySceneCard';
import type { IPropertySceneListProps } from '@/@types/propertySceneEditor';
import type { Id } from '../../../convex/_generated/dataModel';

// ============================================================================
// Sortable Scene Wrapper
// ============================================================================

interface ISortableSceneProps {
  sceneId: Id<'propertyScenes'>;
  children: (dragHandleProps: Record<string, unknown>) => React.ReactNode;
}

function SortableScene({ sceneId, children }: ISortableSceneProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sceneId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : ('auto' as const),
  };

  return (
    <div ref={setNodeRef} style={style} aria-roledescription="sortable scene">
      {children({ ...attributes, ...listeners })}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function PropertySceneList({
  scenes,
  layout,
  processedImages,
  aspectRatio,
  savingSceneIds,
  regeneratingVoiceoverIds,
  regeneratingVideoIds,
  deletingSceneIds,
  onReorder,
  onVoiceoverTextChange,
  onImagePromptChange,
  onImageSelect,
  onPromptEdit,
  onRegenerateVoiceover,
  onRegenerateVideo,
  onPlayVoiceover,
  onExpandVideo,
  onDelete,
  onTemplateChange,
  onNegativePromptChange,
}: IPropertySceneListProps) {
  const sensors = useDndSensors();

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) return;

      const oldIndex = scenes.findIndex(s => s._id === active.id);
      const newIndex = scenes.findIndex(s => s._id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(scenes, oldIndex, newIndex);
        await onReorder(newOrder.map(s => s._id));
      }
    },
    [scenes, onReorder]
  );

  if (scenes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">No scenes yet.</p>
        <p className="text-sm text-muted-foreground mt-1">
          Click "Plan Scenes" to generate scenes for your video.
        </p>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={scenes.map(s => s._id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-4" role="list" aria-label="Video scenes list - drag to reorder">
          {scenes.map(scene => (
            <SortableScene key={scene._id} sceneId={scene._id}>
              {dragHandleProps => (
                <PropertySceneCardMemo
                  scene={scene}
                  layout={layout}
                  processedImages={processedImages}
                  aspectRatio={aspectRatio}
                  dragHandleProps={dragHandleProps}
                  isSaving={savingSceneIds.has(scene._id)}
                  isRegeneratingVoiceover={regeneratingVoiceoverIds.has(scene._id)}
                  isRegeneratingVideo={regeneratingVideoIds.has(scene._id)}
                  isDeleting={deletingSceneIds.has(scene._id)}
                  onVoiceoverTextChange={text => onVoiceoverTextChange(scene._id, text)}
                  onImagePromptChange={prompt => onImagePromptChange(scene._id, prompt)}
                  onImageSelect={() => onImageSelect(scene._id)}
                  onPromptEdit={() => onPromptEdit(scene._id)}
                  onRegenerateVoiceover={() => onRegenerateVoiceover(scene._id)}
                  onRegenerateVideo={() => onRegenerateVideo(scene._id)}
                  onPlayVoiceover={() => onPlayVoiceover(scene._id)}
                  onExpandVideo={() => onExpandVideo(scene._id)}
                  onDelete={() => onDelete(scene._id)}
                  onTemplateChange={onTemplateChange}
                  onNegativePromptChange={onNegativePromptChange}
                />
              )}
            </SortableScene>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export const PropertySceneListMemo = memo(PropertySceneList);
